import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, MapPin, Clock, ChevronRight, Phone, Store, ShoppingBag, Sparkles, Heart, Scissors, Home, Smartphone, Pill, Gem, Droplets, CreditCard, Building2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";
import { isOpenNow } from "../lib/hours";
import { toast } from "sonner";
import { API } from "../App";

const categoryIcons = {
  "Supermarket": ShoppingBag,
  "Fashion": Sparkles,
  "Optical": Sparkles,
  "Electronics": Smartphone,
  "Pharmacy & Wellness": Pill,
  "Home Furnishings": Home,
  "Retail Technology": Store,
  "Dental Clinic": Heart,
  "African Crafts & Souvenirs": Gem,
  "Home Decor": Home,
  "Hair Salon": Scissors,
  "Real Estate": Home,
  "Jewellery": Gem,
  "Water Services": Droplets,
  "M-Pesa Services": Phone,
  "Home Appliances": Home,
  "Dermatology & Aesthetics": Sparkles,
};

export default function HomePage() {
  const [tenants, setTenants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tenantsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/tenants`),
          axios.get(`${API}/categories`)
        ]);
        setTenants(tenantsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (e) {
        toast.error("Couldn't load stores. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const anchorTenant = tenants.find(t => t.is_anchor);
  const featuredTenants = tenants.filter(t => !t.is_anchor);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/directory?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/"
        description="Nyali Plaza — Mombasa's premier shopping destination on Main Nyali Road. Discover Chandarana Supermarket, ATMs, jewellers, fashion, electronics and 17+ stores with contact details and opening hours."
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "ShoppingCenter",
          name: "Nyali Plaza",
          description: "Mombasa's premier shopping destination with over 17 stores and offices, anchored by Chandarana Supermarket.",
          url: "https://nyaliplazaltd.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Main Nyali Road, Opposite Nyali Cinemax",
            addressLocality: "Mombasa",
            addressCountry: "KE",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: -4.049524,
            longitude: 39.683423,
          },
          hasMap: "https://maps.app.goo.gl/FGct29SHeXZefkgY6",
        }, {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Nyali Plaza",
          url: "https://nyaliplazaltd.com",
          logo: "https://nyaliplazaltd.com/og-image.jpg",
        }, {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Nyali Plaza",
          url: "https://nyaliplazaltd.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://nyaliplazaltd.com/directory?search={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }]}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-primary text-white shadow-md">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg leading-tight text-white">Nyali Plaza</h1>
              <p className="text-xs text-blue-100">Directory</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/directory" data-testid="stores-link" className="hidden sm:flex">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 gap-1">
                <Store className="w-4 h-4" />
                All Stores
              </Button>
            </Link>
            <Link to="/atm" data-testid="atm-link" className="hidden sm:flex">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 gap-1">
                <CreditCard className="w-4 h-4" />
                ATM
              </Button>
            </Link>
            <Link to="/space-available" data-testid="space-link" className="hidden sm:flex">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 gap-1">
                <Building2 className="w-4 h-4" />
                Available Spaces
              </Button>
            </Link>
            <Link to="/admin" data-testid="admin-link">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                Admin
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-blue-900 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1624347652947-2982764f0250?w=1200')] bg-cover bg-center opacity-20" />
        <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-12 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-3 tracking-tight">
              Welcome to Nyali Plaza
            </h2>
            <p className="text-blue-100 text-base md:text-lg mb-8 max-w-xl mx-auto">
              Mombasa's premier shopping destination with over 17 stores and offices.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="hero-search-input"
                  type="text"
                  placeholder="Search stores, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 pr-4 rounded-full bg-white border-0 shadow-floating text-foreground text-base"
                />
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Anchor Tenant - Chandarana */}
      {anchorTenant && (
        <section className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 -mt-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Link to={`/tenant/${anchorTenant.slug || anchorTenant.id}`} data-testid="anchor-tenant-card">
              <div className="bg-card rounded-2xl shadow-card-hover overflow-hidden border border-border/50 hover:border-primary/30 transition-all group">
                <div className="relative h-40 md:h-48 overflow-hidden">
                  <img
                    src={anchorTenant.image_url || "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=800"}
                    alt={anchorTenant.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-2">
                      <Sparkles className="w-3 h-3" />
                      Anchor Tenant
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-white">{anchorTenant.name}</h3>
                  </div>
                </div>
                <div className="p-4 md:p-6">
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{anchorTenant.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isOpenNow(anchorTenant.business_hours) ? 'badge-open' : 'badge-closed'}`}>
                        <Clock className="w-3 h-3" />
                        {isOpenNow(anchorTenant.business_hours) ? 'Open Now' : 'Closed'}
                      </span>
                      {anchorTenant.business_hours?.weekdays && (
                        <span className="text-muted-foreground hidden md:inline">{anchorTenant.business_hours.weekdays}</span>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </section>
      )}

      {/* Quick Services - ATM & Space */}
      <section className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-6">
        <div className="grid grid-cols-2 gap-4">
          <Link to="/atm" data-testid="atm-card">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-4 md:p-6 text-white hover:shadow-lg transition-all group"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                <CreditCard className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <h3 className="font-heading font-bold text-lg md:text-xl mb-1">ATM Services</h3>
              <p className="text-green-100 text-xs md:text-sm">24/7 Banking • Multiple Banks</p>
              <ChevronRight className="w-5 h-5 mt-2 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </motion.div>
          </Link>
          
          <Link to="/space-available" data-testid="space-card">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 md:p-6 text-white hover:shadow-lg transition-all group"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <h3 className="font-heading font-bold text-lg md:text-xl mb-1">Space to Let</h3>
              <p className="text-amber-100 text-xs md:text-sm">Office & Retail Available</p>
              <ChevronRight className="w-5 h-5 mt-2 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </motion.div>
          </Link>
        </div>
      </section>

      {/* Quick Categories */}
      <section className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-foreground">Browse Categories</h3>
          <Link to="/directory" className="text-sm text-primary font-medium flex items-center gap-1 hover:underline" data-testid="view-all-categories">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {categories.slice(0, 8).map((category, index) => {
            const IconComponent = categoryIcons[category] || Store;
            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05, duration: 0.3 }}
              >
                <Link
                  to={`/directory?category=${encodeURIComponent(category)}`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="aspect-square flex flex-col items-center justify-center bg-card hover:bg-accent rounded-2xl border border-border/50 hover:border-primary/30 transition-all group p-2"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent flex items-center justify-center mb-2 group-hover:bg-primary/10 transition-colors">
                    <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-center text-muted-foreground group-hover:text-foreground line-clamp-2">{category}</span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Featured Tenants */}
      <section className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 pb-12">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-foreground">Featured Stores</h3>
          <Link to="/directory" className="text-sm text-primary font-medium flex items-center gap-1 hover:underline" data-testid="view-all-stores">
            All Stores <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-card rounded-2xl h-48 animate-pulse border border-border/50" />
            ))
          ) : (
            featuredTenants.map((tenant, index) => (
              <motion.div
                key={tenant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05, duration: 0.4 }}
              >
                <Link to={`/tenant/${tenant.slug || tenant.id}`} data-testid={`tenant-card-${tenant.id}`}>
                  <div className="bg-card rounded-2xl border border-border/50 overflow-hidden hover:border-primary/30 hover:shadow-card-hover transition-all group">
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={tenant.image_url || "https://images.unsplash.com/photo-1635930376155-4ee50db13cca?w=800"}
                        alt={tenant.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 dark:bg-card/90 backdrop-blur-sm text-xs font-medium text-muted-foreground">
                          {tenant.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">{tenant.name}</h4>
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isOpenNow(tenant.business_hours) ? 'badge-open' : 'badge-closed'}`}>
                          <Clock className="w-2.5 h-2.5" />
                          {isOpenNow(tenant.business_hours) ? 'Open' : 'Closed'}
                        </span>
                        {tenant.contacts?.[0]?.phone && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {tenant.contacts[0].phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* Location Section */}
      <section className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-8">
        <h3 className="font-heading text-xl font-bold text-foreground mb-6 text-center">Find Us</h3>
        <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
          <div className="aspect-video w-full">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2798.337142760289!2d39.68342380852522!3d-4.049524595907202!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x184013b041444d3d%3A0x723c764af5068fdb!2sChandarana%20Foodplus%20Nyali%20Plaza!5e1!3m2!1sen!2ske!4v1769035817621!5m2!1sen!2ske"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nyali Plaza Location"
            />
          </div>
          <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground">Nyali Plaza</h4>
                  <p className="text-sm text-muted-foreground">Main Nyali Road, Opposite Nyali Cinemax</p>
                  <p className="text-sm text-muted-foreground">Mombasa, Kenya</p>
                </div>
              </div>
              <a 
                href="https://maps.app.goo.gl/FGct29SHeXZefkgY6" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
              >
                <Button className="rounded-full gap-2">
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Store className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold">Nyali Plaza</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-100">
              <MapPin className="w-4 h-4" />
              <span>Main Nyali Road, Opposite Nyali Cinemax, Mombasa</span>
            </div>
            <a href="https://www.nyaliplaza.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-secondary transition-colors" data-testid="website-link">
              www.nyaliplaza.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
